import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText, AlertTriangle, Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Every question put to the assistant, newest first — SUPER_ADMIN only.
 *
 * The row is deliberately expandable rather than wide: the useful detail is the **generated SQL**
 * sitting next to the answer. A chatbot's dangerous failure is not an error, it is a confident wrong
 * number (an early bug here answered "0 employees present today" because the model guessed a status
 * code), and the only way to catch one after the fact is to read the query that produced it.
 */
interface Row {
  id: string; at: string; who: string; isMerchant: boolean; agentKey: string;
  provider: string; model: string; prompt: string; sql: string; rowCount: number;
  answer: string; tokens: number | null; durationMs: number | null; isError: boolean; error: string;
}
interface Stats { days: number; total: number; failed: number; okRate: number; tokens: number; avgMs: number }

const AGENT_BADGE: Record<string, string> = {
  general: 'bg-slate-100 text-slate-700',
  accountant: 'bg-violet-50 text-violet-700 border-violet-200',
  operations: 'bg-sky-50 text-sky-700 border-sky-200',
};

export function AiChatLogPanel() {
  const { user } = useAuth();
  const isSuperAdmin = user?.kind === 'user' && user.userType === 'SUPER_ADMIN';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [agentKey, setAgentKey] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const { data: stats } = useQuery({
    queryKey: ['ai-log-stats'],
    queryFn: async () => (await api.get<Stats>('/ai-chatbot/logs/stats')).data,
    enabled: isSuperAdmin,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['ai-logs', page, applied, agentKey, onlyErrors],
    queryFn: async () => (await api.get<{ total: number; rows: Row[]; pageSize: number }>('/ai-chatbot/logs', {
      params: { page, pageSize: 50, search: applied || undefined, agentKey: agentKey || undefined, onlyErrors: onlyErrors ? '1' : undefined },
    })).data,
    enabled: isSuperAdmin,
  });

  if (!isSuperAdmin) return <div className="p-6 text-sm text-slate-500">SUPER_ADMIN only.</div>;

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / (data?.pageSize ?? 50)));
  const doSearch = () => { setApplied(search.trim()); setPage(1); };

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-1">
        <ScrollText size={22} className="text-emerald-700" />
        <h2 className="text-xl font-bold">AI Chatbot Log</h2>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        Every question asked of the assistant, who asked it, the SQL it generated and what it answered.
        Open a row to see the query behind a number — that is how a confidently wrong answer gets caught.
      </p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[
            { label: `Questions (${stats.days}d)`, value: stats.total.toLocaleString('en-IN') },
            { label: 'Answered OK', value: `${stats.okRate}%`, warn: stats.okRate < 80 },
            { label: 'Failed', value: stats.failed.toLocaleString('en-IN'), warn: stats.failed > 0 },
            { label: 'Tokens used', value: stats.tokens.toLocaleString('en-IN') },
          ].map((s) => (
            <div key={s.label} className="bg-white border rounded-xl p-3">
              <div className="text-[11px] text-slate-500">{s.label}</div>
              <div className={`text-lg font-bold ${s.warn ? 'text-amber-700' : 'text-slate-900'}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-1 border border-slate-300 rounded-lg px-2 bg-white">
          <Search size={13} className="text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()}
            placeholder="Search question, answer or SQL" className="px-1 py-1.5 text-sm outline-none w-64" />
        </div>
        <button onClick={doSearch} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">Search</button>
        <select value={agentKey} onChange={(e) => { setAgentKey(e.target.value); setPage(1); }}
          className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm">
          <option value="">All agents</option>
          <option value="general">General</option>
          <option value="accountant">Accountant</option>
          <option value="operations">Operational</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={onlyErrors} onChange={(e) => { setOnlyErrors(e.target.checked); setPage(1); }} />
          Failures only
        </label>
        <span className="text-xs text-slate-400 ml-auto">{total.toLocaleString('en-IN')} rows</span>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium whitespace-nowrap">When</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Who</th>
                <th className="px-3 py-2 font-medium whitespace-nowrap">Agent</th>
                <th className="px-3 py-2 font-medium">Question</th>
                <th className="px-3 py-2 font-medium text-right whitespace-nowrap">Rows</th>
                <th className="px-3 py-2 font-medium text-right whitespace-nowrap">Time</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400"><Loader2 size={16} className="animate-spin inline" /></td></tr>}
              {!isLoading && !rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No questions logged yet.</td></tr>}
              {rows.map((r) => (
                <>
                  <tr key={r.id} onClick={() => setOpen(open === r.id ? null : r.id)}
                    className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${r.isError ? 'bg-rose-50/40' : ''}`}>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                      {new Date(r.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {r.who}
                      {r.isMerchant && <span className="ml-1 text-[10px] px-1 rounded bg-amber-100 text-amber-800">merchant</span>}
                    </td>
                    <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${AGENT_BADGE[r.agentKey] ?? AGENT_BADGE.general}`}>{r.agentKey}</span></td>
                    <td className="px-3 py-2">
                      <div className="flex items-start gap-1">
                        {r.isError && <AlertTriangle size={12} className="text-rose-600 mt-0.5 shrink-0" />}
                        <span className="line-clamp-1">{r.prompt}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{r.rowCount}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-slate-500">{r.durationMs != null ? `${(r.durationMs / 1000).toFixed(1)}s` : '—'}</td>
                  </tr>
                  {open === r.id && (
                    <tr key={`${r.id}-d`} className="border-t border-slate-100 bg-slate-50">
                      <td colSpan={6} className="px-3 py-3">
                        <div className="grid gap-2 text-xs">
                          <div><span className="text-slate-500">Answer:</span> <span className="whitespace-pre-wrap">{r.answer || '—'}</span></div>
                          {r.isError && <div className="text-rose-700"><span className="text-slate-500">Error:</span> {r.error}</div>}
                          {r.sql && (
                            <div>
                              <div className="text-slate-500 mb-0.5">Generated SQL</div>
                              <pre className="bg-white border border-slate-200 rounded p-2 overflow-x-auto text-[11px]">{r.sql}</pre>
                            </div>
                          )}
                          <div className="text-slate-400">
                            {r.provider} · {r.model}{r.tokens != null ? ` · ${r.tokens.toLocaleString('en-IN')} tokens` : ''}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="px-2 py-1 border border-slate-300 rounded disabled:opacity-40 inline-flex items-center gap-1"><ChevronLeft size={13} /> Prev</button>
          <span className="text-xs text-slate-500">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
            className="px-2 py-1 border border-slate-300 rounded disabled:opacity-40 inline-flex items-center gap-1">Next <ChevronRight size={13} /></button>
        </div>
      )}
    </div>
  );
}
